import React from 'react';
import { ProductBase } from '@/types/product';
import { Heart } from "lucide-react";
import { useRouter } from 'next/navigation';
import { generateColor } from '@/utils/productMapper';
import StockBadge from '@/components/common/StockBadge';

interface ProductsGridMobileProps {
  className?: string;
  products: ProductBase[];
  onAddToCart: (productId: number) => void;
  onToggleFavorite: (productId: number) => void;
}

// Función para convertir hex a rgba (copiada del componente desktop)
function hexToRgba(hex: string, alpha: number): string {
  if (!hex || typeof hex !== 'string') {
    return 'rgba(255, 255, 255, 1)';
  }
  
  hex = hex.replace('#', '');
  
  if (hex.length !== 6) {
    return 'rgba(255, 255, 255, 1)';
  }
  
  const bigint = parseInt(hex, 16);
  
  if (isNaN(bigint)) {
    return 'rgba(255, 255, 255, 1)';
  }
  
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  
  const validAlpha = Math.max(0, Math.min(1, alpha));
  
  return `rgba(${r}, ${g}, ${b}, ${validAlpha})`;
}

// Función para formatear precios con coma
function formatPrice(price: number): string {
  return price.toFixed(2).replace('.', ',');
}

const ProductsGridMobile: React.FC<ProductsGridMobileProps> = ({
  className = '',
  products,
  onAddToCart,
  onToggleFavorite
}) => {
  const router = useRouter();
  
  // Verificar si hay productos
  if (!products || products.length === 0) {
    return (
      <div data-component-id="src/components/sections/mobile/ProductsGridMobile.tsx:58:6" data-component-path="src/components/sections/mobile/ProductsGridMobile.tsx" data-component-line="58" data-component-file="ProductsGridMobile.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D" className={`flex flex-col items-center justify-center py-8 px-4 ${className}`}>
        <div data-component-id="src/components/sections/mobile/ProductsGridMobile.tsx:59:8" data-component-path="src/components/sections/mobile/ProductsGridMobile.tsx" data-component-line="59" data-component-file="ProductsGridMobile.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22text-center%22%7D" className="text-center">
          <div data-component-id="src/components/sections/mobile/ProductsGridMobile.tsx:60:10" data-component-path="src/components/sections/mobile/ProductsGridMobile.tsx" data-component-line="60" data-component-file="ProductsGridMobile.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22w-12%20h-12%20mx-auto%20mb-3%20rounded-full%20flex%20items-center%20justify-center%22%7D" 
            className="w-12 h-12 mx-auto mb-3 rounded-full flex items-center justify-center"
            style={{ backgroundColor: 'rgba(61, 28, 26, 0.25)' }}
          >
            <svg data-component-id="src/components/sections/mobile/ProductsGridMobile.tsx:64:12" data-component-path="src/components/sections/mobile/ProductsGridMobile.tsx" data-component-line="64" data-component-file="ProductsGridMobile.tsx" data-component-name="svg" data-component-content="%7B%22elementName%22%3A%22svg%22%2C%22className%22%3A%22w-6%20h-6%22%7D" 
              className="w-6 h-6" 
              fill="none" 
              stroke="#3D1C1A" 
              viewBox="0 0 24 24"
            >
              <path data-component-id="src/components/sections/mobile/ProductsGridMobile.tsx:70:14" data-component-path="src/components/sections/mobile/ProductsGridMobile.tsx" data-component-line="70" data-component-file="ProductsGridMobile.tsx" data-component-name="path" data-component-content="%7B%22elementName%22%3A%22path%22%7D" 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" 
              />
            </svg>
          </div>
          <h3 data-component-id="src/components/sections/mobile/ProductsGridMobile.tsx:78:10" data-component-path="src/components/sections/mobile/ProductsGridMobile.tsx" data-component-line="78" data-component-file="ProductsGridMobile.tsx" data-component-name="h3" data-component-content="%7B%22elementName%22%3A%22h3%22%2C%22className%22%3A%22text-base%20mb-2%22%2C%22textContent%22%3A%22No%20se%20encontraron%20productos%20disponibles%20para%20esta%20categor%C3%ADa%22%7D" 
            className="text-base mb-2"
            style={{
              fontFamily: "'TT_Norms_Pro-Bold', Helvetica",
              fontWeight: '700',
              color: '#3D1C1A'
            }}
          >
            No se encontraron productos disponibles para esta categoría
          </h3>

          <div data-component-id="src/components/sections/mobile/ProductsGridMobile.tsx:89:10" data-component-path="src/components/sections/mobile/ProductsGridMobile.tsx" data-component-line="89" data-component-file="ProductsGridMobile.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22text-xs%22%2C%22textContent%22%3A%22Intenta%20con%20otra%20categor%C3%ADa%20o%20vuelve%20m%C3%A1s%20tarde.%22%7D" 
            className="text-xs"
            style={{
              fontFamily: "'TT_Norms_Pro-Regular', Helvetica",
              fontWeight: '400',
              color: '#3D1C1A'
            }}
          >
            Intenta con otra categoría o vuelve más tarde.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-component-id="src/components/sections/mobile/ProductsGridMobile.tsx:105:4" data-component-path="src/components/sections/mobile/ProductsGridMobile.tsx" data-component-line="105" data-component-file="ProductsGridMobile.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D" className={`grid grid-cols-2 gap-3 items-stretch ${className}`}>
      {products.map((product, index) => {
        // Calcular descuento (igual que en desktop)
        const currentPriceNumber = parseFloat(String(product?.priceFinalInc));
        const originalPriceNumber =
          product?.priceOriginalInc !== undefined
            ? parseFloat(String(product?.priceOriginalInc))
            : undefined;
        const hasDiscount =
          originalPriceNumber !== undefined &&
          !isNaN(originalPriceNumber) &&
          originalPriceNumber > currentPriceNumber;
        const discountPercent = hasDiscount
          ? Math.round(((originalPriceNumber - currentPriceNumber) / originalPriceNumber) * 100)
          : 0;

        // Usar backgroundColor y backgroundOpacity específicos si están disponibles
        const backgroundColor = generateColor(index) || '#FFFFFF';
        const backgroundOpacity = 1;
        
        const backgroundStyle = {
          backgroundColor: hexToRgba(backgroundColor, backgroundOpacity)
        };

        return (
          <div data-component-id="src/components/sections/mobile/ProductsGridMobile.tsx:130:10" data-component-path="src/components/sections/mobile/ProductsGridMobile.tsx" data-component-line="130" data-component-file="ProductsGridMobile.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22rounded-%5B12px%5D%20p-3%20relative%20min-h-%5B200px%5D%20shadow-sm%20flex%20flex-col%22%7D" 
            key={product.id}
            className="rounded-[12px] p-3 relative min-h-[200px] shadow-sm flex flex-col"
            style={backgroundStyle}
          >
            <StockBadge data-component-id="src/components/sections/mobile/ProductsGridMobile.tsx:135:12" data-component-path="src/components/sections/mobile/ProductsGridMobile.tsx" data-component-line="135" data-component-file="ProductsGridMobile.tsx" data-component-name="StockBadge" data-component-content="%7B%22elementName%22%3A%22StockBadge%22%2C%22className%22%3A%22top-0%20left-0%20scale-75%20origin-top-left%22%7D" productId={product.id} className="top-0 left-0 scale-75 origin-top-left" />

            {/* Badge de descuento */}
            {hasDiscount && (
              <div data-component-id="src/components/sections/mobile/ProductsGridMobile.tsx:139:14" data-component-path="src/components/sections/mobile/ProductsGridMobile.tsx" data-component-line="139" data-component-file="ProductsGridMobile.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22absolute%20top-2%20right-2%20z-10%20bg-%5B%23fcc00a%5D%20px-2%20py-1%20rounded-full%20shadow-md%22%7D" className="absolute top-2 right-2 z-10 bg-[#fcc00a] px-2 py-1 rounded-full shadow-md">
                <span data-component-id="src/components/sections/mobile/ProductsGridMobile.tsx:140:16" data-component-path="src/components/sections/mobile/ProductsGridMobile.tsx" data-component-line="140" data-component-file="ProductsGridMobile.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-%5B%233d1c1a%5D%20text-%5B10px%5D%20font-bold%22%2C%22textContent%22%3A%22-%20%25%22%7D"
                  className="text-[#3d1c1a] text-[10px] font-bold"
                  style={{
                    fontFamily: "'TT_Norms_Pro-Bold', Helvetica",
                    fontWeight: '700',
                  }}
                >
                  -{discountPercent}%
                </span>
              </div>
            )}

            {/* Botón de favoritos */}
            {/* TODO: Añadir el icono de favorito
            <button
              onClick={() => onToggleFavorite(product.id)}
              className="absolute top-2 right-2 z-10"
            >
              <Heart 
                className={`w-4 h-4 ${product.isFavorite ? 'text-red-500 fill-red-500' : 'text-[#3d1c1a]'}`} 
              />
            </button>
            */}

            {/* Imagen del producto */}
            <div data-component-id="src/components/sections/mobile/ProductsGridMobile.tsx:165:12" data-component-path="src/components/sections/mobile/ProductsGridMobile.tsx" data-component-line="165" data-component-file="ProductsGridMobile.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20justify-center%20items-center%20h-24%20mb-2%20flex-shrink-0%20overflow-hidden%22%7D" className="flex justify-center items-center h-24 mb-2 flex-shrink-0 overflow-hidden">
              <img data-component-id="src/components/sections/mobile/ProductsGridMobile.tsx:166:14" data-component-path="src/components/sections/mobile/ProductsGridMobile.tsx" data-component-line="166" data-component-file="ProductsGridMobile.tsx" data-component-name="img" data-component-content="%7B%22elementName%22%3A%22img%22%2C%22className%22%3A%22max-h-full%20max-w-full%20object-contain%20cursor-pointer%22%7D"
                src={product.images?.find(p => p.isMain)?.src ?? product.image}
                alt={product.images?.find(p => p.isMain)?.alt ?? product.title}
                width={80}
                height={80}
                className="max-h-full max-w-full object-contain cursor-pointer"
                onClick={() => router.push(`/${product.slug}`)}
              />
            </div>

            {/* Información del producto */}
            <div data-component-id="src/components/sections/mobile/ProductsGridMobile.tsx:177:12" data-component-path="src/components/sections/mobile/ProductsGridMobile.tsx" data-component-line="177" data-component-file="ProductsGridMobile.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex-1%20flex%20flex-col%20min-h-0%22%7D" className="flex-1 flex flex-col min-h-0">
              <h3 data-component-id="src/components/sections/mobile/ProductsGridMobile.tsx:178:14" data-component-path="src/components/sections/mobile/ProductsGridMobile.tsx" data-component-line="178" data-component-file="ProductsGridMobile.tsx" data-component-name="h3" data-component-content="%7B%22elementName%22%3A%22h3%22%2C%22className%22%3A%22text-xs%20leading-4%20text-global-1%20mb-1%20cursor-pointer%20line-clamp-2%20min-h-8%22%7D" 
                className="text-xs leading-4 text-global-1 mb-1 cursor-pointer line-clamp-2 min-h-8"
                style={{
                  fontFamily: "'TT_Norms_Pro-Bold', Helvetica",
                  fontWeight: '700'
                }}
                onClick={() => router.push(`/${product.slug}`)}
                title={product.title}
              >
                {product.title}
              </h3>
              {product.brand && (
                <p data-component-id="src/components/sections/mobile/ProductsGridMobile.tsx:190:16" data-component-path="src/components/sections/mobile/ProductsGridMobile.tsx" data-component-line="190" data-component-file="ProductsGridMobile.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-xs%20text-global-1%20mb-1%20truncate%22%7D" 
                  className="text-xs text-global-1 mb-1 truncate"
                  style={{
                    fontFamily: "'TT_Norms_Pro-Regular', Helvetica",
                    fontWeight: '400'
                  }}
                >
                  {product.brand}
                </p>
              )}
              {product.productCombinations && product.productCombinations.length > 0 && (
                <p data-component-id="src/components/sections/mobile/ProductsGridMobile.tsx:201:16" data-component-path="src/components/sections/mobile/ProductsGridMobile.tsx" data-component-line="201" data-component-file="ProductsGridMobile.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22leading-5%20text-gray-500%20text-xs%20truncate%22%7D"
                  className="leading-5 text-gray-500 text-xs truncate"
                  style={{
                    fontFamily: "'TT_Norms_Pro-Regular', Helvetica",
                    fontWeight: '400',
                  }}
                >
                  {product.productCombinations
                    .find((combination) => combination.isDefault)
                    ?.productOptions?.map((option) => option.name)
                    .join(', ')}
                </p>
              )}
              
              {/* Precio */}
              <div data-component-id="src/components/sections/mobile/ProductsGridMobile.tsx:216:14" data-component-path="src/components/sections/mobile/ProductsGridMobile.tsx" data-component-line="216" data-component-file="ProductsGridMobile.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20flex-col%20mt-auto%20pr-10%22%7D" className="flex flex-col mt-auto pr-10">
                  <span data-component-id="src/components/sections/mobile/ProductsGridMobile.tsx:217:18" data-component-path="src/components/sections/mobile/ProductsGridMobile.tsx" data-component-line="217" data-component-file="ProductsGridMobile.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-sm%20text-global-1%20whitespace-nowrap%22%2C%22textContent%22%3A%22%E2%82%AC%2Fud.%22%7D" 
                    className="text-sm text-global-1 whitespace-nowrap"
                    style={{
                      fontFamily: "'TT_Norms_Pro-Bold', Helvetica",
                      fontWeight: '700'
                    }}
                  >
                    {formatPrice(currentPriceNumber)} €/ud.
                  </span>
                  {hasDiscount && originalPriceNumber && (
                    <span data-component-id="src/components/sections/mobile/ProductsGridMobile.tsx:227:20" data-component-path="src/components/sections/mobile/ProductsGridMobile.tsx" data-component-line="227" data-component-file="ProductsGridMobile.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-%5B10px%5D%20text-%5B%233d1c1a%5D%2F60%20line-through%22%2C%22textContent%22%3A%22%E2%82%AC%2Fud.%22%7D"
                      className="text-[10px] text-[#3d1c1a]/60 line-through"
                      style={{ 
                        fontFamily: "'TT_Norms_Pro-Regular', Helvetica", 
                        fontWeight: 400 
                      }}
                    >
                      {formatPrice(originalPriceNumber)} €/ud.
                    </span>
                  )}
              </div>
            </div>

            <button data-component-id="src/components/sections/mobile/ProductsGridMobile.tsx:240:12" data-component-path="src/components/sections/mobile/ProductsGridMobile.tsx" data-component-line="240" data-component-file="ProductsGridMobile.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22absolute%20bottom-3%20right-3%20bg-global-9%20rounded-full%20w-8%20h-8%20flex%20items-center%20justify-center%20flex-shrink-0%22%7D"
              onClick={() => onAddToCart(product.id)}
              className="absolute bottom-3 right-3 bg-global-9 rounded-full w-8 h-8 flex items-center justify-center flex-shrink-0"
            >
              <img data-component-id="src/components/sections/mobile/ProductsGridMobile.tsx:244:14" data-component-path="src/components/sections/mobile/ProductsGridMobile.tsx" data-component-line="244" data-component-file="ProductsGridMobile.tsx" data-component-name="img" data-component-content="%7B%22elementName%22%3A%22img%22%2C%22src%22%3A%22%2Fimages%2Fimg_plus.svg%22%2C%22alt%22%3A%22A%C3%B1adir%22%7D"
                src="/images/img_plus.svg"
                alt="Añadir"
                width={16}
                height={16}
              />
            </button>
          </div>
        );
      })}
    </div>
  );
};

export default ProductsGridMobile; 